import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function Hisa() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="high-interest-savings-accounts-hisa" />
          {/* Header */}
          <div className="text-center mb-12 pt-4"> {/* Added padding top */}
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">High-Interest Savings Accounts (HISA)</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Earn More on Your Savings with Safety and Accessibility
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is a HISA? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is a High-Interest Savings Account?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                A High-Interest Savings Account (HISA) is a type of bank account designed to offer a more competitive interest rate on your deposits compared to traditional chequing or basic savings accounts. While still providing safety and easy access to your funds, HISAs help your cash savings grow faster.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                They are ideal for holding emergency funds, saving for short-term goals (like a vacation or down payment), or temporarily parking cash before making longer-term investment decisions.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How It Works</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Account Opening:</strong> You open a HISA account at a financial institution (bank, credit union, or online-only bank).
                </li>
                <li>
                  <strong>Deposits:</strong> You deposit funds into the account.
                </li>
                <li>
                  <strong>Interest Accrual:</strong> The financial institution pays interest on your balance, typically calculated daily and paid monthly. Interest rates are variable and can change based on market conditions (e.g., Bank of Canada rates).
                </li>
                <li>
                  <strong>Accessibility:</strong> Funds are generally readily accessible for withdrawal or transfer, although some HISAs might have slightly fewer transaction features than a standard chequing account.
                </li>
                <li>
                  <strong>Safety:</strong> Deposits in HISAs at member institutions are typically insured by the Canada Deposit Insurance Corporation (CDIC) or provincial credit union deposit guarantee corporations, up to certain limits per depositor per category.
                </li>
                 <li>
                  <strong>Taxation:</strong> Interest earned in a non-registered HISA is considered taxable income and must be reported on your tax return annually. HISAs can also be held within registered accounts like TFSAs or RRSPs, where the interest earned would be tax-sheltered according to the rules of that specific account type.
                 </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Higher Interest Rates:</strong> Earn significantly more interest than traditional savings accounts.</li>
                <li><strong>Safety and Security:</strong> Principal is protected, and deposits are typically insured up to applicable limits.</li>
                <li><strong>Accessibility:</strong> Easy access to your funds when needed.</li>
                <li><strong>Simplicity:</strong> Straightforward and easy to understand.</li>
                <li><strong>Ideal for Short-Term Goals:</strong> Perfect for saving where capital preservation is key.</li>
                <li><strong>Emergency Fund Vehicle:</strong> A safe and accessible place to keep 3-6 months of living expenses.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Important factors to keep in mind:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Variable Interest Rates:</strong> Rates are not fixed and can fluctuate. Promotional rates may expire.</li>
                <li><strong>Taxation (Non-Registered):</strong> Interest earned outside registered plans is taxed annually.</li>
                <li><strong>Inflation Risk:</strong> Interest rates may not always keep pace with inflation, potentially eroding purchasing power over the long term.</li>
                <li><strong>Transaction Limits/Fees:</strong> Some HISAs may have limits on free transactions or charge fees for certain services.</li>
                <li><strong>Not an Investment:</strong> HISAs are for saving, not long-term investment growth aiming for higher returns (which involves risk).</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is a HISA Right for You?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">A HISA is a fundamental financial tool suitable for:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Building an emergency fund.</li>
                <li>Saving for short-term goals (less than 3-5 years) like a down payment, car, or vacation.</li>
                <li>Holding cash temporarily while deciding on investment strategies.</li>
                <li>Risk-averse savers who prioritize capital preservation.</li>
                <li>Anyone wanting to earn a better return on their cash savings than a standard account offers.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Make Your Savings Work Harder</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Ensure your cash savings are earning the best possible rates while remaining safe and accessible. Let's review HISA options available through our partners.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Explore HISA Options
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget /> // Removed onClose prop
      )}
    </MarketingLayout>
  );
}
